// Form dinamico dirigido pelo field_schema do canal (Fase 1 — fecha o ciclo).
// Le GET /api/channels/:slug -> field_schema (meta-schema) -> renderiza inputs ->
// grava em channel_publications.field_overrides via PATCH. Campos required_for_publish
// sao marcados; o gating real (422) acontece no publish (backend).
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input, Textarea } from "./ui/input";
import { Label } from "./ui/label";
import { Select } from "./ui/select";

type FieldType = "text" | "textarea" | "number" | "boolean" | "url" | "select";
interface DynamicField {
  key: string;
  label: string;
  type: FieldType;
  required_for_publish?: boolean;
  options?: string[];
}
interface ChannelDetail {
  slug: string;
  field_schema?: { version?: number; fields?: unknown[] };
}

function parseDynamicFields(schema: ChannelDetail["field_schema"]): DynamicField[] {
  const raw = Array.isArray(schema?.fields) ? schema!.fields! : [];
  const out: DynamicField[] = [];
  for (const f of raw) {
    if (typeof f !== "object" || f === null) continue; // ignora formato legado (strings)
    const ff = f as Record<string, unknown>;
    if (typeof ff.key !== "string" || !ff.key) continue;
    out.push({
      key: ff.key,
      label: typeof ff.label === "string" ? ff.label : ff.key,
      type: (typeof ff.type === "string" ? ff.type : "text") as FieldType,
      required_for_publish: ff.required_for_publish === true,
      options: Array.isArray(ff.options) ? ff.options.map(String) : undefined,
    });
  }
  return out;
}

export function DynamicFieldsCard({
  slug,
  sku,
  initialOverrides,
  disabled,
}: {
  slug: string;
  sku: string;
  initialOverrides: Record<string, unknown>;
  disabled: boolean;
}): JSX.Element | null {
  const qc = useQueryClient();
  const { data: channel } = useQuery<ChannelDetail>({
    queryKey: ["channel-detail", slug],
    queryFn: async () => (await api.get<ChannelDetail>(`/api/channels/${slug}`)).data,
  });

  const fields = useMemo(() => parseDynamicFields(channel?.field_schema), [channel]);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const init: Record<string, string> = {};
    for (const f of fields) {
      const v = initialOverrides?.[f.key];
      init[f.key] = v === undefined || v === null ? "" : String(v);
    }
    setValues(init);
  }, [fields, initialOverrides]);

  const mut = useMutation({
    mutationFn: async () => {
      // Monta field_overrides completo (merge dos conhecidos do schema).
      const overrides: Record<string, unknown> = { ...(initialOverrides ?? {}) };
      for (const f of fields) {
        const raw = values[f.key] ?? "";
        if (raw === "") {
          delete overrides[f.key];
          continue;
        }
        overrides[f.key] =
          f.type === "number" ? Number(raw) : f.type === "boolean" ? raw === "true" : raw;
      }
      await api.patch(`/api/channels/${slug}/products/${sku}`, { field_overrides: overrides });
    },
    onSuccess: () => {
      toast.success("Campos do canal salvos");
      qc.invalidateQueries({ queryKey: ["product", slug, sku] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  // Sem campos dinamicos no canal -> nao renderiza nada (UI limpa).
  if (fields.length === 0) return null;

  return (
    <section className="card mt-4">
      <div className="mb-3">
        <h3 className="row__title">Campos do canal</h3>
        <p className="field__hint mt-0.5">
          Campos próprios deste canal (field_schema). Os marcados com * são obrigatórios para publicar.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key}>
            <Label htmlFor={`df-${f.key}`}>
              {f.label}
              {f.required_for_publish ? <span className="text-destructive"> *</span> : null}
            </Label>
            {f.type === "textarea" ? (
              <Textarea
                id={`df-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                rows={3}
                disabled={disabled}
              />
            ) : f.type === "boolean" ? (
              <Select
                id={`df-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                disabled={disabled}
              >
                <option value="">—</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </Select>
            ) : f.type === "select" ? (
              <Select
                id={`df-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                disabled={disabled}
              >
                <option value="">—</option>
                {(f.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id={`df-${f.key}`}
                type={f.type === "number" ? "number" : f.type === "url" ? "url" : "text"}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                disabled={disabled}
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <Button variant="secondary" onClick={() => mut.mutate()} loading={mut.isPending} disabled={disabled}>
          Salvar campos do canal
        </Button>
      </div>
    </section>
  );
}
